import { notification } from "antd";
import { CSSObject } from "styled-components";

import { ReportMapAxios } from "../Contracts/Axios/ReportMapAxios";
import { IReportMapAxios } from "../Contracts";
import { IPolygon } from "../Models/IPolygon";
import { IRetailMatchTradeAreaBlockGroup } from "../Models/Models";
import {
    SET_MAP_CENTER,
    SET_MAP_LOADING,
    CLEAR_CURRENT_GOOGLE_MAP_REF,
    SET_MAP_MARKERS,
    CREATE_NEW_GOOGLE_MAP,
    SET_MAP_ZOOM,
    CLEAR_GOOGLE_MAP,
    SET_MAP_ANCHOR_WINDOW,
    SET_MAP_STYLE_OPTIONS,
    SET_MAP_HEIGHT,
    SET_PENDING_MAP_CENTER_EVENT,
    SET_MAP_IS_READY,
    CREATE_NEW_RETAIL_MATCH_OVERVIEW_MAP,
    CREATE_NEW_RETAIL_MATCH_MODAL_MAP,
    SET_RETAIL_MATCH_OVERVIEW_MAP_MARKERS,
    SET_MAP_STYLES,
} from "./Types";
import { MapStyleEnum } from "../Enums/Enums";

export const createNewGoogleMap = (map) => ({
    type: CREATE_NEW_GOOGLE_MAP,
    data: map,
});

export const createNewRetailMatchOverviewMap = (map) => ({
    type: CREATE_NEW_RETAIL_MATCH_OVERVIEW_MAP,
    data: map,
});

export const setRetailMatchOverMapMarkers = (
    markers: google.maps.Marker[]
) => ({
    type: SET_RETAIL_MATCH_OVERVIEW_MAP_MARKERS,
    data: markers,
});

export const createNewRetailMatchModalMap = (map) => ({
    type: CREATE_NEW_RETAIL_MATCH_MODAL_MAP,
    data: map,
});

export const clearCurrentGoogleMapRef = () => ({
    type: CLEAR_CURRENT_GOOGLE_MAP_REF,
    data: null,
});

export const setMapCenter = (center: google.maps.LatLng) => ({
    type: SET_MAP_CENTER,
    data: center,
});

// Calling this with `true` is equivalent to telling the map to re-center using
// the currently stored lat/lng.
//
// This is distinct from `setMapCenter`, which only updates the stored lat/lng
// to be used on the next centering.
//
// This allows us to work within the declarative flow of React/Redux instead of
// mixing state with imperative Google Map calls.
//
// NOTE: Due to hooks, `setMapCenter` may in some instances cause the map
// to re-center when new coordinates are passed in.
//
// NOTE 2: We're not using this at the moment for the GPS Trade Area map
// because it causes the Deck GL dots to render in slightly wrong positions at first.
export const setPendingMapCenterEvent = (pending: boolean) => ({
    type: SET_PENDING_MAP_CENTER_EVENT,
    data: pending,
});

export const setMapZoom = (zoom: number) => ({
    type: SET_MAP_ZOOM,
    data: zoom,
});

export const setMapStyleOptions = (styleOption: MapStyleEnum) => ({
    type: SET_MAP_STYLE_OPTIONS,
    data: styleOption,
});

export const setMapStyles = (style: CSSObject) => ({
    type: SET_MAP_STYLES,
    data: style,
});

export const setMapHeight = (height: string) => ({
    type: SET_MAP_HEIGHT,
    data: height,
});

export const setMapMarkers = (markers: google.maps.Marker[]) => ({
    type: SET_MAP_MARKERS,
    data: markers,
});

export const clearMapMarkers = (markers: google.maps.Marker[]) => {
    markers.forEach((marker) => marker.setMap(null));

    return { type: SET_MAP_MARKERS, data: markers };
};

export const setMapAnchorWindow = (anchorWindow: google.maps.InfoWindow) => ({
    type: SET_MAP_ANCHOR_WINDOW,
    data: anchorWindow,
});

export const setShowLoadingMarker = (loading: boolean) => ({
    type: SET_MAP_LOADING,
    data: loading,
});

export const setIsMapReady = (isReady: boolean) => ({
    type: SET_MAP_IS_READY,
    data: isReady,
});

export const cleanMap = () => ({
    type: CLEAR_GOOGLE_MAP,
});

export const getTradeAreaPolygonsFromBlockGroups = async (
    blockGroups: IRetailMatchTradeAreaBlockGroup[]
) => {
    const reportMapAxios: IReportMapAxios = new ReportMapAxios();
    const blockGroupIds: string[] = blockGroups.map((bg) => bg.id);

    try {
        const response: IPolygon[] = await reportMapAxios.GetBlockgroupPolygons(
            blockGroupIds
        );
        return response;
    } catch (error) {
        notification.error({
            message: "Error",
            description: error.message,
            duration: null, // user must close
        });
        return null;
    }
};
